import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Box, Button, Icon, theme } from '@factorialco/deprecated-design-system'
import { AnimatePresence } from 'framer-motion'
import {
  EmployeesBulkContractPageDocument,
  EmployeesBulkContractPageQuery,
} from 'generated/resources/graphql'
import moment from 'moment'
import { useDocumentTitle } from 'usehooks-ts'

import { observer } from 'mobx-react'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCreateCompensationsRolloutByFix } from 'modules/employees/hooks/useCreateCompensationsRolloutByFix'
import Page from 'modules/layout/components/Page'
import Loading from 'modules/layout/components/Page/Loading'

import BulkFixWizard from './BulkFixWizard'
import { WizardData, mapToMutationVariables } from './BulkFixWizard/state'
import {
  type EmployeeMeta,
  type LegalEntityMeta,
  NO_POLICY_ID,
  type PayrollPolicyMeta,
} from './types'

type LocationStateType = { employeesIds?: number[] } | null

interface Props {}

const BulkFixContractsPage: React.FC<Props> = () => {
  const [showExitDialog, setShowExitDialog] = React.useState(false)

  useDocumentTitle(
    i18n.t('employees.bulk_edit.fix_conditions.doc_title', {
      preserveTitleOnUnmount: false,
    })
  )

  const {
    company: { id: companyId },
    access,
  } = useCurrentSession()

  const location = useLocation()
  const locationState: LocationStateType = location.state

  const rawEmployeeIds = new Set<number>(locationState?.employeesIds ?? [])

  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { createCompensationsRolloutByFix } = useCreateCompensationsRolloutByFix()

  const { data } = useQuery(EmployeesBulkContractPageDocument, {
    variables: {
      activeOn: dateToString(moment(), 'backend'),
      employeeIds: [...rawEmployeeIds],
    },
  })

  const extractedData = useUnsafeMemo(
    function extractGqlData(): ExtractedEmployeeMeta | null {
      if (!data) return null

      const {
        employeesMeta,
        legalEntitiesMeta: mappedLegalEntitiesMeta,
        policiesMeta: mappedPoliciesMeta,
      } = data.employees.employees.reduce<IntermediateExtractedEmployeeMeta>(reduceMeta, {
        employeesMeta: [],
        legalEntitiesMeta: {},
        policiesMeta: {},
      })

      return {
        employeesMeta: employeesMeta,
        legalEntitiesMeta: Object.values(mappedLegalEntitiesMeta),
        policiesMeta: Object.values(mappedPoliciesMeta),
      }
    },
    [data]
  )

  useEffect(() => {
    track(trackingEvents.fixAgreementInBulk.startFixAgreement, {
      companyId,
      acessId: access.id,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isLoading = !extractedData

  const handleExit = () => {
    // Don't trust 'back' in case we use navigation within the process.
    navigate(navigation.employees.list.fullPath())
  }

  async function handleSubmitCompensations(wizardState: WizardData) {
    const response = await createCompensationsRolloutByFix(
      mapToMutationVariables(companyId, wizardState)
    )
    const result = handleResult(response, (errors) => {
      app.setFlash({
        type: 'wrong',
        message: errors.messages ?? i18n.t('errors.generic_error'),
        time: 3000,
      })
    })

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('employees.bulk_edit.fix_conditions.success_message', {
          count: wizardState.affectedEmployeeIds.size,
        }),
      })
      track(trackingEvents.fixAgreementInBulk.fixAgreementSave, {
        companyId,
        acessId: access.id,
      })
      navigate(navigation.employees.list.fullPath())
    }
  }

  return (
    <Page>
      <Page.Header>
        <Box position="absolute" style={{ top: theme.dimensions.s24, right: theme.dimensions.s24 }}>
          <Box
            cursor="pointer"
            onClick={() => setShowExitDialog(true)}
            aria-label={i18n.t('common.close')}
          >
            <Icon.Small icon="close" />
          </Box>
        </Box>
      </Page.Header>
      <ErrorBoundary>
        <Page.Body withoutPadding>
          {isLoading ? (
            <Box height="full" justifyContent="center" alignItems="center">
              <Loading />
            </Box>
          ) : (
            <BulkFixWizard
              targetEmployees={extractedData.employeesMeta}
              allowedLegalEntities={extractedData.legalEntitiesMeta}
              allowedPayrollPolicies={extractedData.policiesMeta}
              onSubmit={handleSubmitCompensations}
            />
          )}
          {/* Cancel dialog */}
          <AnimatePresence>
            {showExitDialog && (
              <Modal
                title={i18n.t('employees.bulk_edit.fix_conditions.cancel_modal.title')}
                description={i18n.t('employees.bulk_edit.fix_conditions.cancel_modal.description')}
                onClose={() => setShowExitDialog(false)}
                onSubmit={handleExit}
                footer={
                  <>
                    <Button onClick={() => setShowExitDialog(false)} type="secondary">
                      {i18n.t('common.cancel')}
                    </Button>
                    <Button submit>{i18n.t('common.exit')}</Button>
                  </>
                }
              />
            )}
          </AnimatePresence>
        </Page.Body>
      </ErrorBoundary>
    </Page>
  )
}

export default observer(BulkFixContractsPage)

interface ExtractedEmployeeMeta {
  employeesMeta: EmployeeMeta[]
  legalEntitiesMeta: LegalEntityMeta[]
  policiesMeta: PayrollPolicyMeta[]
}

function extractEmployeeMeta(gqlData: EmployeeConnectionsInput): EmployeeMeta {
  const {
    id,
    legalEntity,
    payrollPolicyEmployee,
    access,
    fullName: name,
    contractVersions,
  } = gqlData

  return {
    id,
    name,
    avatar: access?.avatar ?? undefined,
    // The query on backend should prevent these ever being undefined
    legalEntityId: legalEntity?.id ?? NO_POLICY_ID,
    payrollPolicyId: payrollPolicyEmployee?.payrollPolicy?.id ?? -1,
    activeContractId: contractVersions[0]?.id ?? undefined,
    canEdit: contractVersions[0]?._policies?.edit?.can ?? false,
  }
}

function extractLegalEntityMeta(gqlData: EmployeeConnectionsInput): LegalEntityMeta | null {
  if (!gqlData.legalEntity) return null

  const { id, currency, legalName: name } = gqlData.legalEntity
  return {
    id,
    currency,
    name,
  }
}

function extractPayrollPolicyMeta(gqlData: EmployeeConnectionsInput): PayrollPolicyMeta {
  if (!gqlData.payrollPolicyEmployee?.payrollPolicy?.name)
    return {
      id: NO_POLICY_ID,
      name: i18n.t('employees.bulk_edit.fix_conditions.steps.scope.no_policy_option'),
      recurrence: '',
      startsOn: '',
    }

  const { id, name, recurrence, startsOn } = gqlData.payrollPolicyEmployee.payrollPolicy
  return {
    id,
    name,
    recurrence,
    startsOn,
  }
}

type EmployeeConnectionsInput = EmployeesBulkContractPageQuery['employees']['employees'][0]

interface IntermediateExtractedEmployeeMeta {
  employeesMeta: EmployeeMeta[]
  legalEntitiesMeta: { [leId: string]: LegalEntityMeta }
  policiesMeta: { [ppId: string]: PayrollPolicyMeta }
}

function reduceMeta(
  previousValue: IntermediateExtractedEmployeeMeta,
  employeeGqlData: EmployeeConnectionsInput
): IntermediateExtractedEmployeeMeta {
  const employeeMeta = extractEmployeeMeta(employeeGqlData)

  // filter out employees without active contracts
  if (employeeMeta.activeContractId && employeeMeta.canEdit) {
    previousValue.employeesMeta.push(employeeMeta)
  }

  const legalEntityMeta = extractLegalEntityMeta(employeeGqlData)
  if (legalEntityMeta && !(legalEntityMeta.id in previousValue.legalEntitiesMeta)) {
    previousValue.legalEntitiesMeta[legalEntityMeta.id] = legalEntityMeta
  }

  const policyMeta = extractPayrollPolicyMeta(employeeGqlData)
  if (!(policyMeta.id in previousValue.policiesMeta)) {
    previousValue.policiesMeta[policyMeta.id] = policyMeta
  }

  return previousValue
}
